"use client";

import Link from "next/link";
import Image from "next/image";
import { Flex, Typography } from "antd";

import { pushToDataLayer } from "@/lib/gtm";
import "./style.css";

export type LandingLocation = {
  name: string;
  slug: string;
  image?: string;
  hasColor?: boolean;
  href?: string;
};

interface LandingLocationButtonsProps {
  locations: LandingLocation[];
}

const getImageMode = (src?: string): "optimized" | "native" | null => {
  if (!src) return null;
  if (src.startsWith("/")) return "optimized";

  try {
    const url = new URL(src);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.protocol === "https:" && url.hostname === "imagedelivery.net"
      ? "optimized"
      : "native";
  } catch {
    return null;
  }
};

export default function LandingLocationButtons({
  locations,
}: LandingLocationButtonsProps) {
  return (
    <Flex gap={12} wrap="wrap" className="landing-locations">
      {locations.map((location) => {
        const imageMode = getImageMode(location.image);

        return (
          <Link
            key={location.slug}
            href={location.href || `/appointment/${location.slug}`}
            {...(location.href
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className="location-card"
            style={{
              backgroundColor: imageMode ? undefined : "#222",
            }}
            onClick={() =>
              pushToDataLayer("appointment_start", {
                source: "landing_location_button",
                location_name: location.name,
              })
            }
          >
            {location.image && imageMode === "optimized" ? (
              <Image
                src={location.image}
                alt=""
                fill
                sizes="(max-width: 768px) calc((100vw - 60px) / 2), 150px"
                aria-hidden="true"
              />
            ) : location.image && imageMode === "native" ? (
              // API-managed images can come from providers outside the fixed
              // Next.js allowlist. Keep those resilient without sacrificing
              // optimization for known Cloudflare/local assets.
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={location.image}
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
            ) : null}
            <div className="location-overlay" />
            <Typography.Text className="location-name" strong>
              {location.name}
            </Typography.Text>
          </Link>
        );
      })}
    </Flex>
  );
}
